import {PrototypeLogic} from '@fantasmagames/slot/src/logic/prototype/PrototypeLogic';
import {Symbols} from 'src/reels/Symbols';
import {Communicator} from '@fantasmagames/slot/src/logic/Communicator';
import {GamePrototypeCommunicator} from 'src/logic/prototype/GamePrototypeCommunicator';
import {SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {
	AvalancheResult,
	PrototypeSpinResult,
	PrototypeWinLine,
} from '@fantasmagames/slot/src/logic/prototype/PrototypeSpinResult';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ServerEvents} from '@fantasmagames/prototype/src/reels/ServerAvalancheReelsRound';
import {SpinResult} from '@fantasmagames/slot/src/logic/SpinResult';
import {GamePrototypeSpinResult} from 'src/logic/prototype/GamePrototypeSpinResult';
import {Logic} from '@fantasmagames/slot/src/logic/Logic';
import {Game} from 'src/Game';
import {RecodeToPrototypeConverter} from 'src/logic/recode/RecodeToPrototypeConverter';
import {Action} from '@fantasmagames/slot/src/logic/Action';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';

export interface Respin {
	position: SymbolPosition;
	removedPositions: SymbolPosition[];
}

export interface GamePrototypeAvalancheResult extends AvalancheResult {
	respins: Respin[];
	wildMeter: number;
	runningTotal: number;
}

export interface GamePrototypeWinLine extends PrototypeWinLine {
	isRightToLeft: boolean;
}

export class GamePrototypeLogic extends PrototypeLogic {
	public static readonly BN_SYMBOLS_TO_TRIGGER_FS: number = 3;

	private _currentSpinWin: number;

	public static readonly PAYLINES: number[][] = [
		[3, 3, 3, 3, 3], // Line 1
		[4, 4, 4, 4, 4], // Line 2
		[5, 5, 5, 5, 5], // Line 3
		[3, 4, 5, 4, 3], // Line 4
		[5, 4, 3, 4, 5], // Line 5
		[4, 3, 3, 3, 4], // Line 6
		[4, 5, 5, 5, 4], // Line 7
		[3, 3, 4, 5, 5], // Line 8
		[5, 5, 4, 3, 3], // Line 9
		[4, 5, 4, 3, 4], // Line 10
	];

	/* tslint:disable */
	protected intializeServerSymbols(): void {
		this._serverSymbols['WR'] = Symbols.WR;
		this._serverSymbols['M1'] = Symbols.M1;
		this._serverSymbols['M2'] = Symbols.M2;
		this._serverSymbols['M3'] = Symbols.M3;
		this._serverSymbols['M4'] = Symbols.M4;
		this._serverSymbols['M5'] = Symbols.M5;
		this._serverSymbols['F6'] = Symbols.F6;
		this._serverSymbols['F7'] = Symbols.F7;
		this._serverSymbols['F8'] = Symbols.F8;
		this._serverSymbols['F9'] = Symbols.F9;
		this._serverSymbols['F10'] = Symbols.F10;
		this._serverSymbols['BN'] = Symbols.BN;
		this._serverSymbols['RS'] = Symbols.RS;

		RecodeToPrototypeConverter.serverSymbolNames[Symbols.WR] = 'WR';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.M1] = 'M1';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.M2] = 'M2';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.M3] = 'M3';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.M4] = 'M4';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.M5] = 'M5';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.F6] = 'F6';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.F7] = 'F7';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.F8] = 'F8';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.F9] = 'F9';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.F10] = 'F10';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.BN] = 'BN';
		RecodeToPrototypeConverter.serverSymbolNames[Symbols.RS] = 'RS';
	}

	/* tslint:enable */

	protected createCommunicator(communicator: Communicator): void {
		this._communicator = new GamePrototypeCommunicator();
		// (this._communicator as GamePrototypeCommunicator).addFakeResponse('');
}

	protected parseAvalanches(data: any): void {
		this._avalanches = [];
		let avalancheIndex: number = 0;
		for (const avalancheData of data.generations) {
			const avalanche: GamePrototypeAvalancheResult = {
				initialSymbols: [],
				symbolsAfterRemoval: [],
				newSymbols: [],
				win: 0,
				winnings: [],
				respins: [],
				runningTotal: 0,
				wildMeter: 0,
			};

			let reelIndex: number = 0;
			for (const reel of avalancheData.symbols) {
				avalanche.initialSymbols.push([]);
				let rowIndex: number = 0;
				for (const sym of reel) {
					avalanche.initialSymbols[reelIndex].push(new GameSymbol(this._serverSymbols[sym],
						{width: 1, height: 1},
						{x: reelIndex, y: rowIndex}));
					rowIndex++;
				}
				reelIndex++;
			}

			if (avalancheData.win) {
				avalanche.win = avalancheData.win;
			}

			if (avalancheData.hasOwnProperty('runningTotal')) {
				avalanche.runningTotal = avalancheData.runningTotal;
			}

			if (avalancheData.hasOwnProperty('wildMeter')) {
				avalanche.wildMeter = avalancheData.wildMeter;
			}

			const removedSymbols: SymbolPosition[] = [];
			if (avalancheData.winnings) {
				for (const win of avalancheData.winnings) {

					const winning: GamePrototypeWinLine = {
						winType: win[0],
						winPositions: [],
						winLength: 0,
						win: win[2],
						lineIndex: win[1],
						winSymbol: this._serverSymbols[win[4]],
						isRightToLeft: win[5] === 1,
					};

					for (const wl of win[3]) {
						winning.winPositions.push({x: wl.x, y: wl.y});
						const shouldAddSymbol: boolean = removedSymbols.findIndex((item) => {
							return item.x === wl.x && item.y === wl.y;
						}) < 0;
						if (shouldAddSymbol) {
							removedSymbols.push({x: wl.x, y: wl.y});
						}
					}
					winning.winLength = winning.winPositions.length;

					avalanche.winnings.push(winning);
				}
			}

			if (avalancheData.removal) {
				for (const respinData of avalancheData.removal) {
					const respin: Respin = {position: {x: respinData.x, y: respinData.y}, removedPositions: []};
					for (const pos of respinData.removal) {
						respin.removedPositions.push({x: pos.x, y: pos.y});

						const shouldAddSymbol: boolean = removedSymbols.findIndex((item) => {
							return item.x === pos.x && item.y === pos.y;
						}) < 0;
						if (shouldAddSymbol) {
							removedSymbols.push({x: pos.x, y: pos.y});
						}
					}

					removedSymbols.push({x: respin.position.x, y: respin.position.y});
					avalanche.respins.push(respin);
				}
			}

			const winSymbolsOnEachPosition: number[] = [0, 0, 0, 0, 0, 0];
			for (const removedSymbol of removedSymbols) {
				winSymbolsOnEachPosition[removedSymbol.x]++;
			}
			reelIndex = 0;
			for (reelIndex = 0; reelIndex < winSymbolsOnEachPosition.length; reelIndex++) {
				const totalWinningSymbols: number = winSymbolsOnEachPosition[reelIndex];
				avalanche.newSymbols.push([]);
				for (let symbolIndex = 0; symbolIndex < totalWinningSymbols; symbolIndex++) {
					const symVal: string = data.generations[avalancheIndex + 1].symbols[reelIndex][symbolIndex];
					avalanche.newSymbols[reelIndex].push(new GameSymbol(this._serverSymbols[symVal], {
						width: 1,
						height: 1,
					}, {x: -1, y: -1}));
				}
			}

			if (avalanche.respins.length > 0) {
				this._roundTypes.push(ServerEvents.RESPIN);
			} else if (avalanche.winnings.length > 0) {
				this._roundTypes.push(ServerEvents.AVALANCHE);
			}

			this._avalanches.push(avalanche);

			avalancheIndex++;
		}
	}

	protected parseOtherData(data: any): void {
		if (data.hasOwnProperty('spinWin')) {
			this._currentSpinWin = data.spinWin;
		} else {
			this._currentSpinWin = 0;
		}
	}

	protected createSpinResult(): SpinResult {
		return new GamePrototypeSpinResult();
	}

	protected setResultsForCurrentRound(): void {
		super.setResultsForCurrentRound();

		(this.getPrototypeSpinResults() as GamePrototypeSpinResult).setRespinData(this._avalanches[this._currentRound] as GamePrototypeAvalancheResult);

	}

	public getLastAvalancheRunningTotal(): number {
		if (this._avalanches && this._avalanches.length > 0) {
			return (this._avalanches[this._avalanches.length - 1] as GamePrototypeAvalancheResult).runningTotal * Logic.CENT_TO_A_DOLLAR;
		}

		return 0;
	}

	public getLastAvalancheMultiplier(): number {
		if (this._avalanches && this._avalanches.length > 0) {
			const wildMeter: number = (this._avalanches[this._avalanches.length - 1] as GamePrototypeAvalancheResult).wildMeter;

			if (this.isInFreeSpins) {
				return 1 + Math.floor(wildMeter / 3) * 2;
			} else {
				return 1 + Math.floor(wildMeter / 3);
			}
		}

		return 1;
	}

	public getNextAvalancheRunningTotalInCents(): number {
		if (this._avalanches && this._avalanches.length > 0) {
			if (this._currentRound + 1 >= this._avalanches.length) {
				return (this._avalanches[this._avalanches.length - 1] as GamePrototypeAvalancheResult).runningTotal;
			}

			return (this._avalanches[this._currentRound + 1] as GamePrototypeAvalancheResult).runningTotal;
		}

		return 0;
	}

	public hasWinsToHighlight(): boolean {
		const winningSymbols: SymbolPosition[] = this.getAllWinningSymbolsFromCurrentRound();

		return winningSymbols.length > 0;
	}

	public getAllWinningSymbolsFromCurrentRound(): SymbolPosition[] {
		const winPositions: SymbolPosition[] = [];

		for (const wl of this._avalanches[this._currentRound].winnings) {
			winPositions.push(...wl.winPositions);
		}

		return winPositions;
	}

	public getAllWinLinesFromCurrentRound(): PrototypeWinLine[] {
		return this._avalanches[this._currentRound].winnings;
	}

	public getCurrentReelsSymbol(pos: SymbolPosition): GameSymbol {
		return this._avalanches[this._currentRound].initialSymbols[pos.x][pos.y];
	}

	public getCurrentRoundWinningWilds(): SymbolPosition[] {
		const winWildsPositions: SymbolPosition[] = [];

		for (const wl of this._avalanches[this._currentRound].winnings) {
			for (const winPos of wl.winPositions) {
				const gs: GameSymbol = this._avalanches[this._currentRound].initialSymbols[winPos.x][winPos.y];
				if (gs.sym === Symbols.WR || gs.sym === Symbols.TOP_WR) {
					const hasItem: boolean = winWildsPositions.findIndex( (item) => {
						return item.x === winPos.x && item.y === winPos.y;
					}) >= 0;
					if (!hasItem) {
						winWildsPositions.push({x: winPos.x, y: winPos.y});
					}
				}
			}
		}

		return winWildsPositions;

	}

	public get currentSpinWin(): number {
		return this._currentSpinWin * Logic.CENT_TO_A_DOLLAR;
	}

	public getCurrentRoundTotalWinNoMultiplier(): number {
		let totalWin: number = 0;
		for (const avalanche of this._avalanches) {
			totalWin += avalanche.win;
		}

		return totalWin * Logic.CENT_TO_A_DOLLAR;
	}

	public getFinalSymbolsPosition(): AvalancheResult {
		return this._avalanches[this._avalanches.length - 1];
	}

	public isFreeSpinsTrigger(): boolean {
		const symbols = this.getFinalSymbolsPosition().initialSymbols;
		let bnSymbolsCount: number = 0;

		symbols.forEach(reel => {
			reel.forEach(symbol => {
				if (symbol.sym === Symbols.BN && symbol.position.y >= 3) {
					bnSymbolsCount++;
				}
			});
		});

		return bnSymbolsCount >= GamePrototypeLogic.BN_SYMBOLS_TO_TRIGGER_FS;
	}

	public reactOnInit(data: any): void {
		super.reactOnInit(data);

		if (data.generations) {
			this.reactOnSpin(data);
		}
	}

	public forceSetNextAction(action: Action): void {
		this._nextAction = action;
	}
}



// WEBPACK FOOTER //
// ./src/logic/prototype/GamePrototypeLogic.ts